import React, { useState } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { Toaster } from '@/components/ui/sonner';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import ListPropertyModal from '@/components/ListPropertyModal';
import ProtectedRoute from '@/components/ProtectedRoute';
import CookieConsent from '@/components/CookieConsent';
import MaintenanceBanner from '@/components/MaintenanceBanner';
import PromoBanner from '@/components/growth/PromoBanner';
import { getUser } from '@/utils/auth';

// Core Pages
import Home from '@/pages/Home';
import Login from '@/pages/Login';
import Register from '@/pages/Register';
import InviteLanding from '@/pages/InviteLanding';
import ReferralDashboard from '@/pages/ReferralDashboard';
import ForgotPassword from '@/pages/ForgotPassword';
import ResetPassword from '@/pages/ResetPassword';
import AuthCallback from '@/pages/AuthCallback';
import Dashboard from '@/pages/Dashboard';
import ComingSoon from '@/pages/ComingSoon';

// Buy/Sell Pages
import RealEstate from '@/pages/RealEstate';
import PropertyDetails from '@/pages/PropertyDetails';
import SellIndividual from '@/pages/SellIndividual';
import RentIndividual from '@/pages/RentIndividual';
import PostRequirement from '@/pages/PostRequirement';
import AgentPortal from '@/pages/AgentPortal';
import DeveloperPortal from '@/pages/DeveloperPortal';
import GetLegalOpinion from '@/pages/GetLegalOpinion';

// Rental Pages
import CorporateRentals from '@/pages/CorporateRentals';
import IndividualRentals from '@/pages/IndividualRentals';
import VacationRentals from '@/pages/VacationRentals';
import ListProperty from '@/pages/ListProperty';
import ZenSpace from '@/pages/ZenSpace';
import FindRentals from '@/pages/FindRentals';
import RentalDetails from '@/pages/RentalDetails';

// Service Pages
import InteriorDesign from '@/pages/InteriorDesignNew';
import InteriorDesignZEngine from '@/pages/InteriorDesignZEngine';
import PropertyManagement from '@/pages/PropertyManagement';
import HomeLoans from '@/pages/HomeLoans';

// Interior Design Sub-pages
import CorporateInterior from '@/pages/CorporateInterior';
import BusinessInterior from '@/pages/BusinessInterior';
import HospitalityInterior from '@/pages/HospitalityInterior';
import HealthcareInterior from '@/pages/HealthcareInterior';

// Content Pages
import AboutUs from '@/pages/AboutUs';
import Inspiration from '@/pages/Inspiration';
import InspirationDetail from '@/pages/InspirationDetail';
import Blogs from '@/pages/Blogs';
import BlogDetail from '@/pages/BlogDetail';
import Advertise from '@/pages/Advertise';
import PriestServices from '@/pages/PriestServices';

// Vendor/Services Pages
import ServicesLanding from '@/pages/ServicesLanding';
import PriestServicesNew from '@/pages/PriestServicesNew';
import VendorProfile from '@/pages/VendorProfile';
import AgentProfile from '@/pages/AgentProfile';
import DeveloperProfile from '@/pages/DeveloperProfile';

// Legal Pages
import TermsConditions from '@/pages/TermsConditions';
import PrivacyPolicy from '@/pages/PrivacyPolicy';
import Disclaimer from '@/pages/Disclaimer';

// Help Center
import HelpCenter from '@/pages/HelpCenter';

// SEO Pages
import LocationLanding from '@/pages/LocationLanding';
import InteriorPortfolio from '@/pages/InteriorPortfolio';

// Internal Apps Redirect Pages (Admin, Employee & Partner moved to separate apps)
import AdminRedirect from '@/pages/AdminRedirect';
import EmployeeRedirect from '@/pages/EmployeeRedirect';
import PartnerRedirect from '@/pages/PartnerRedirect';
import PartnerSubscriptions from '@/pages/PartnerSubscriptions';

// Project Landing (Auto-generated from Architectural Builder)
import ProjectLanding from '@/pages/ProjectLanding';

import '@/App.css';

function AppRouter({ onListPropertyClick }) {
  const location = useLocation();
  
  // Check URL fragment for session_id synchronously
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  // Check if current path is a redirect route (no navbar/footer)
  const isRedirectRoute = (location.pathname.startsWith('/admin') ||
                          location.pathname.startsWith('/employee') ||
                          (location.pathname.startsWith('/partner') && !location.pathname.startsWith('/partner/pricing')) ||
                          location.pathname.startsWith('/agent-dashboard') ||
                          location.pathname.startsWith('/developer-dashboard'));

  // Maps current route to a business unit for PromoBanner scoping.
  // Returns "__none__" for pages where no BU-scoped promo is relevant.
  const routeBusinessUnit = () => {
    const p = location.pathname;
    if (p.startsWith('/interior-design') || p.startsWith('/interiors')) return 'interior';
    if (p.startsWith('/sell/agent') || p.startsWith('/sell/developer') || p.startsWith('/partner/pricing')) return 'partner_subscription';
    if (p.startsWith('/buy') || p.startsWith('/property/') || p.startsWith('/project/') || p.startsWith('/sell/individual')) return 'real_estate';
    if (p.startsWith('/services')) return 'services';
    return '__none__'; // home, login, rentals, etc. — show no promo banner
  };

  // Audience (user_type) for promo scoping. Prefer the logged-in user's type;
  // otherwise derive from the page context so audience-scoped promos (e.g. agent-only)
  // don't leak onto pages targeted at a different audience.
  const routeAudience = () => {
    const p = location.pathname;
    const u = getUser();
    if (u && (u.user_type || u.role)) return u.user_type || u.role;
    if (p.startsWith('/sell/agent')) return 'agent';
    if (p.startsWith('/sell/developer')) return 'developer';
    if (p.startsWith('/interior-design') || p.startsWith('/interiors') ||
        p.startsWith('/buy') || p.startsWith('/property/') || p.startsWith('/project/') ||
        p.startsWith('/sell/individual') || p.startsWith('/services')) return 'consumer';
    return null; // generic (e.g. /partner/pricing) — show all promos for the BU
  };

  // Redirect routes without navbar/footer - these apps are now separate
  if (isRedirectRoute) {
    return (
      <Routes>
        <Route path="/admin-login" element={<AdminRedirect />} />
        <Route path="/admin-dashboard/*" element={<AdminRedirect />} />
        <Route path="/employee-login" element={<EmployeeRedirect />} />
        <Route path="/employee-dashboard/*" element={<EmployeeRedirect />} />
        <Route path="/partner-login" element={<PartnerRedirect />} />
        <Route path="/partner-portal" element={<PartnerRedirect />} />
        <Route path="/agent-dashboard/*" element={<PartnerRedirect />} />
        <Route path="/developer-dashboard/*" element={<PartnerRedirect />} />
      </Routes>
    );
  }

  return (
    <>
      <MaintenanceBanner />
      <Navbar onListPropertyClick={onListPropertyClick} />
      <PromoBanner businessUnit={routeBusinessUnit()} userType={routeAudience()} />
      <Routes>
        {/* Core Routes */}
        <Route path="/" element={<Home />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/i/:code" element={<InviteLanding />} />
        <Route path="/referrals" element={<ReferralDashboard />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/agent-login" element={<PartnerRedirect />} />
        <Route path="/developer-login" element={<PartnerRedirect />} />
        
        {/* Buy Routes */}
        <Route path="/buy/properties" element={<RealEstate />} />
        <Route path="/buy/post-requirement" element={<PostRequirement />} />
        <Route path="/buy/legal-opinion" element={<GetLegalOpinion />} />
        <Route path="/property/:propertyId" element={<PropertyDetails />} />
        
        {/* Project Landing Page (Auto-generated) */}
        <Route path="/project/:projectId" element={<ProjectLanding />} />
        
        {/* Sell Routes */}
        <Route path="/sell/individual" element={<SellIndividual />} />
        <Route path="/sell/agent" element={<AgentPortal />} />
        <Route path="/sell/developer" element={<DeveloperPortal />} />
        <Route path="/partner/pricing" element={<PartnerSubscriptions />} />
        
        {/* Rental Routes */}
        <Route path="/rent/individual" element={<RentIndividual />} />
        <Route path="/rentals/corporate" element={<CorporateRentals />} />
        <Route path="/rentals/vacation" element={<VacationRentals />} />
        <Route path="/rentals/individual" element={<IndividualRentals />} />
        <Route path="/rentals/find" element={<FindRentals />} />
        <Route path="/rentals/:rentalId" element={<RentalDetails />} />
        <Route path="/rentals/zen-space" element={<ZenSpace />} />
        <Route path="/list-property" element={<ListProperty />} />
        
        {/* Interior Design Routes */}
        <Route path="/interior-design" element={<InteriorDesignZEngine />} />
        <Route path="/interior-design/z-engine" element={<InteriorDesignZEngine />} />
        <Route path="/interior-design/smart-estimator" element={<InteriorDesign />} />
        <Route path="/interior-design/corporate" element={<CorporateInterior />} />
        <Route path="/interior-design/business" element={<BusinessInterior />} />
        <Route path="/interior-design/hospitality" element={<HospitalityInterior />} />
        <Route path="/interior-design/healthcare" element={<HealthcareInterior />} />
        
        {/* Service Routes */}
        <Route path="/property-management" element={<PropertyManagement />} />
        <Route path="/home-loans" element={<HomeLoans />} />
        
        {/* Content Routes */}
        <Route path="/about" element={<AboutUs />} />
        <Route path="/inspiration" element={<Inspiration />} />
        <Route path="/inspiration/:slug" element={<InspirationDetail />} />
        <Route path="/blogs" element={<Blogs />} />
        <Route path="/blogs/:slug" element={<BlogDetail />} />
        <Route path="/advertise" element={<Advertise />} />
        <Route path="/buy/priest-services" element={<PriestServices />} />
        
        {/* Services Marketplace Routes */}
        <Route path="/services" element={<ServicesLanding />} />
        <Route path="/services/priest" element={<PriestServicesNew />} />
        <Route path="/vendor/:slugId" element={<VendorProfile />} />
        <Route path="/agent/:slugId" element={<AgentProfile />} />
        <Route path="/developer/:slugId" element={<DeveloperProfile />} />
        
        {/* Legacy Routes (redirects) */}
        <Route path="/real-estate" element={<RealEstate />} />
        
        {/* Legal Routes */}
        <Route path="/terms-conditions" element={<TermsConditions />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/disclaimer" element={<Disclaimer />} />
        
        {/* Help Center */}
        <Route path="/help" element={<HelpCenter />} />
        
        {/* Interior Portfolio Pages (before location catch-all) */}
        <Route path="/interiors/project/:slug" element={<InteriorPortfolio />} />
        <Route path="/interiors/projects" element={<Inspiration />} />
        
        {/* SEO Location Landing Pages */}
        <Route path="/buy/:city" element={<LocationLanding />} />
        <Route path="/rent/:city" element={<LocationLanding />} />
        <Route path="/interiors/:city" element={<LocationLanding />} />
        
        {/* Protected Routes */}
        <Route 
          path="/dashboard" 
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          } 
        />
      </Routes>
      <Footer />
    </>
  );
}

function App() {
  const [listPropertyModalOpen, setListPropertyModalOpen] = useState(false);
  
  return (
    <HelmetProvider>
    <div className="App">
      <BrowserRouter>
        <AppRouter onListPropertyClick={() => setListPropertyModalOpen(true)} />
        <ListPropertyModal 
          isOpen={listPropertyModalOpen} 
          onClose={() => setListPropertyModalOpen(false)} 
        />
        <CookieConsent />
        <Toaster position="top-right" />
      </BrowserRouter>
    </div>
    </HelmetProvider>
  );
}

export default App;
